/**
 * useTenantBranding / useUpdateTenantBranding / useUploadBrandingAsset /
 * useDeleteBrandingAsset — Brief 5 A3 Branding (Sprint Frontend Brief 5 · Tela 4).
 *
 * Consome o módulo tenants (tenant-branding.controller):
 *   - GET    /v1/tenant-branding                 → { data: SerializedTenantBranding }
 *   - PATCH  /v1/tenant-branding                 → atualiza escalares (parcial)
 *   - POST   /v1/tenant-branding/:kind (multipart)→ upload logo/favicon
 *   - DELETE /v1/tenant-branding/:kind           → remove asset
 *
 * Gate server-side: `tenant-config-read` (GET) / `tenant-config-manage` (escrita);
 * escrita exige tier >= Team. As mutations invalidam a query e devolvem o
 * serializer fresco (sem optimistic-lock — PATCH não tem If-Match).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type {
  AssetKind,
  SerializedTenantBranding,
  UpdateTenantBrandingRequest,
} from '../features/admin/branding/types';

const QUERY_KEY = ['tenant-branding'] as const;
const STALE_TIME = 30_000;

export function useTenantBranding(enabled: boolean): {
  data: SerializedTenantBranding | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.getTenantBranding().then((r) => r.data),
    enabled,
    staleTime: STALE_TIME,
    retry: 1,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useUpdateTenantBranding(): ReturnType<
  typeof useMutation<SerializedTenantBranding, Error, UpdateTenantBrandingRequest>
> {
  const qc = useQueryClient();
  return useMutation<SerializedTenantBranding, Error, UpdateTenantBrandingRequest>({
    mutationFn: (body) => api.updateTenantBranding(body).then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
    },
  });
}

interface AssetVars {
  kind: AssetKind;
  file: File;
}

export function useUploadBrandingAsset(): ReturnType<
  typeof useMutation<SerializedTenantBranding, Error, AssetVars>
> {
  const qc = useQueryClient();
  return useMutation<SerializedTenantBranding, Error, AssetVars>({
    mutationFn: ({ kind, file }) => api.uploadTenantBrandingAsset(kind, file).then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
    },
  });
}

export function useDeleteBrandingAsset(): ReturnType<
  typeof useMutation<SerializedTenantBranding, Error, AssetKind>
> {
  const qc = useQueryClient();
  return useMutation<SerializedTenantBranding, Error, AssetKind>({
    mutationFn: (kind) => api.deleteTenantBrandingAsset(kind).then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
    },
  });
}
